import { Box, ListItemText, MenuItem, styled } from "@mui/material";
import { colors, GhostPlaceholder, Typography } from "@procore/core-react";
import { useTranslation } from "react-i18next";

import { CORE_TOOLBAR_IDS } from "contexts/toolbars";
import { CORE_WINDOW_IDS, useDispatchWindows, windowsActions } from "contexts/windows";

import { NestedMenuItem } from "./NestedMenuItem";
import { useProcoreStamp } from "./useProcoreStamp";

const StyledMenuItem = styled(MenuItem)({
    display: "flex",
    justifyContent: "space-between",
    paddingTop: "2px",
    paddingBottom: "2px",
});

export function ProcoreCanvasContextMenuStamp() {
    const { t } = useTranslation();
    const dispatchWindows = useDispatchWindows();

    const stampData = useProcoreStamp({
        openProperties: () => {
            dispatchWindows(
                windowsActions.toggleWindow(CORE_WINDOW_IDS.PROPERTIES, {
                    elementId: CORE_TOOLBAR_IDS.DETAILS,
                    placement: "right",
                }),
            );
        },
    });

    if (!stampData) {
        return null;
    }

    const {
        stamp,
        obj,
        isLoading,
        flyToDisabled,
        hideSimilarDisabled,
        selectSimilarDisabled,
        multipleObjectsSelected,
        highlightedCount,
        menuItems,
        hide,
        hideSimilar,
        selectSimilar,
        isolate,
        isolateInXRayMode,
        isIsolated,
        isIsolatedInXRay,
        unisolate,
        unisolateFromXRayMode,
        viewProperties,
        zoomToSelection,
        addSectionPlane,
        executeMenuItem,
    } = stampData;

    if (multipleObjectsSelected) {
        return (
            <Box
                sx={{
                    pointerEvents: "auto",
                    width: 248,
                }}
            >
                <ListItemText sx={{ padding: "2px 14px" }}>
                    <Typography intent="h3">{t("objectsSelectedCount", { count: highlightedCount })}</Typography>
                </ListItemText>
                <StyledMenuItem disableRipple onClick={hide} disabled={stamp.data.object === undefined}>
                    <ListItemText>
                        <Typography intent="body">{t("hideSelected")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
                <StyledMenuItem disableRipple onClick={viewProperties} disabled={flyToDisabled}>
                    <ListItemText>
                        <Typography intent="body">{t("viewProperties")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
                <StyledMenuItem
                    disableRipple
                    onClick={() => {
                        void zoomToSelection();
                    }}
                    disabled={flyToDisabled}
                >
                    <ListItemText>
                        <Typography intent="body">{t("zoomToSelection")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
                <NestedMenuItem label={t("isolate")} parentMenuOpen>
                    <StyledMenuItem
                        disableRipple
                        onClick={isIsolated ? unisolate : isolate}
                        disabled={stamp.data.object === undefined}
                    >
                        <ListItemText>
                            <Typography intent="body">{isIsolated ? t("unisolate") : t("isolate")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                    <StyledMenuItem
                        disableRipple
                        onClick={isIsolatedInXRay ? unisolateFromXRayMode : isolateInXRayMode}
                        disabled={stamp.data.object === undefined}
                    >
                        <ListItemText>
                            <Typography intent="body">
                                {isIsolatedInXRay ? t("unisolateFromXRayMode") : t("isolateInXRayMode")}
                            </Typography>
                        </ListItemText>
                    </StyledMenuItem>
                </NestedMenuItem>
                {menuItems.map((item) => (
                    <StyledMenuItem disableRipple key={item.id} onClick={() => executeMenuItem(item)}>
                        <ListItemText>
                            <Typography intent="body">{item.label}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                ))}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                pointerEvents: "auto",
                width: 248,
            }}
        >
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <ListItemText sx={{ padding: "2px 14px", overflowWrap: "anywhere" }}>
                    <Typography intent="h3">
                        {obj?.name || (stamp.data.object ? `Object ${stamp.data.object}` : "")}
                    </Typography>
                </ListItemText>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={hide} disabled={stamp.data.object === undefined}>
                    <ListItemText>
                        <Typography intent="body">{t("hideSelected")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={hideSimilar} disabled={hideSimilarDisabled}>
                    <ListItemText>
                        <Typography intent="body">{t("hideSimilar")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={viewProperties} disabled={flyToDisabled}>
                    <ListItemText>
                        <Typography intent="body">{t("viewProperties")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={addSectionPlane} disabled={!stamp.data.normal}>
                    <ListItemText>
                        <Typography intent="body">{t("addSectionPlane")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem
                    disableRipple
                    onClick={() => {
                        void zoomToSelection();
                    }}
                    disabled={flyToDisabled}
                >
                    <ListItemText>
                        <Typography intent="body">{t("zoomToSelection")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem
                    disableRipple
                    disabled={selectSimilarDisabled}
                    onClick={selectSimilar}
                    sx={{ paddingTop: "2px", paddingBottom: "2px" }}
                >
                    <ListItemText>
                        <Typography intent="body">{t("selectSimilar")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <NestedMenuItem label={t("isolate")} parentMenuOpen>
                    <StyledMenuItem
                        disableRipple
                        onClick={isIsolated ? unisolate : isolate}
                        disabled={stamp.data.object === undefined}
                    >
                        <ListItemText>
                            <Typography intent="body">{isIsolated ? t("unisolate") : t("isolate")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                    <StyledMenuItem
                        disableRipple
                        onClick={isIsolatedInXRay ? unisolateFromXRayMode : isolateInXRayMode}
                        disabled={stamp.data.object === undefined}
                    >
                        <ListItemText>
                            <Typography intent="body">
                                {isIsolatedInXRay ? t("unisolateFromXRayMode") : t("isolateInXRayMode")}
                            </Typography>
                        </ListItemText>
                    </StyledMenuItem>
                    {
                        //temporarily hiding because the functionality is not ready
                        /* <StyledMenuItem disableRipple disabled>
                        <ListItemText>
                            <Typography intent="body">{t("isolateInSectionBox")}</Typography>
                        </ListItemText>
                    </StyledMenuItem> */
                    }
                </NestedMenuItem>
            )}
            {menuItems.map((item) =>
                isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem disableRipple key={item.id} onClick={() => executeMenuItem(item)}>
                        <ListItemText>
                            <Typography intent="body">{item.label}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                ),
            )}
        </Box>
    );
}

function MenuGhostPlaceholder() {
    return (
        <Box sx={{ padding: "2px 12px" }}>
            <GhostPlaceholder startGradientColor={colors.gray40} endGradientColor={colors.gray94} />
        </Box>
    );
}
